import type { CurrencyCode } from '@/shared/lib/currency';

export enum RollsStatus {
  WAITING = 'WAITING',
  COUNTDOWN = 'COUNTDOWN',
  SPINNING = 'SPINNING',
  FINISHED = 'FINISHED',
}

export interface RollsContribution {
  currency: string;
  amount: number;
  amountUsd?: number | null;
  /** Set only for GIFT contributions — metadata of the staked gift. */
  giftName?: string | null;
  giftImage?: string | null;
}

export interface RollsPlayer {
  id: string;
  username: string;
  avatar?: string;
  betAmount: number;
  color: string;
  winChance: number;
  totalsByCurrency: Record<string, number>;
  contributions: RollsContribution[];
}

export interface RollsRound {
  id: string;
  index: number;
  status: RollsStatus;
  players: RollsPlayer[];
  totalPot: number;
  potsByCurrency: Record<string, number>;
  currencyMode: string;
  fxRates: Record<string, number>;
  winnerId: string | null;
  countdownSecondsLeft: number | null;
  spinTargetAngle: number | null;
  serverSeedHash: string | null;
}

export interface PaginatedHistory {
  items: RollsRoundHistory[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface AutoplayConfig {
  isActive: boolean;
  betAmount: number;
  /** Total rounds to auto-bet before stopping. */
  numberOfGames: number;
  /** Rounds already auto-bet in the current session. */
  gamesPlayed: number;
}

export interface RollsState {
  currentRound: RollsRound;
  onlineCounts: Record<string, number>;
  selectedCurrency: CurrencyCode;
  autoplay: AutoplayConfig;
}

export interface RollsRoundHistory {
  id: string;
  index: number;
  winnerId: string;
  winnerUsername: string;
  winnerColor: string;
  winnerAvatar: string | null;
  winnerBet: number;
  totalPot: number;
  winnerPayout: number;
  winnerPayoutsByCurrency: Record<string, number>;
  potsByCurrency: Record<string, number>;
  currencyMode: string;
  playerCount: number;
  winChance: number;
}

export interface BetDetail {
  userId: string | null;
  username: string;
  avatar: string | null;
  color: string;
  amount: number;
  totalsByCurrency: Record<string, number>;
  contributions: RollsContribution[];
  isWinner: boolean;
}

export interface RoundDetail {
  id: string;
  totalPot: number;
  winnerPayout: number;
  winnerPayoutsByCurrency: Record<string, number>;
  potsByCurrency: Record<string, number>;
  currencyMode: string;
  fxRates: Record<string, number>;
  playerCount: number;
  winnerId: string | null;
  winnerUsername: string | null;
  winnerColor: string | null;
  winnerAvatar: string | null;
  winnerBet: number;
  winChance: number;
  bets: BetDetail[];
  finishedAt: string | null;
  serverSeed: string | null;
  serverSeedHash: string | null;
  clientSeed: string | null;
  nonce: number | null;
  resultFloat: number | null;
}
